import { useState } from 'react';
import {
  Box, Stack, TextField, IconButton, Switch, Typography, CircularProgress, Tooltip, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useAnforderungKategorien,
  useCreateAnforderungKategorie,
  useUpdateAnforderungKategorie,
  useDeleteAnforderungKategorie,
  type AnforderungKategorie,
} from '../../api/hooks/useAnforderungKategorien';
import { useNotifications } from '../../context/NotificationContext';
import { formatApiError } from '../../api/client';

/**
 * Mandantenspezifische Verwaltung der Anforderungs-Kategorien (Stammdaten).
 * CRUD gegen /api/v1/anforderung-kategorien (tenant-scoped). Beim ersten Laden
 * seedet das Backend 5 Defaults (seed-once). Duplikat-Name → 409 (notify).
 */
export function AnforderungKategorienManager() {
  const { data, isLoading } = useAnforderungKategorien();
  const createMut = useCreateAnforderungKategorie();
  const { notify } = useNotifications();
  const [neu, setNeu] = useState('');

  const rows = ((data as unknown as { data?: AnforderungKategorie[] } | undefined)?.data ?? [])
    .slice()
    .sort((a, b) => a.sort - b.sort);

  const add = async () => {
    const name = neu.trim();
    if (!name) return;
    try {
      await createMut.mutateAsync({ name, sort: (rows[rows.length - 1]?.sort ?? 0) + 1 });
      setNeu('');
    } catch (e) { notify(formatApiError(e), 'error'); }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>;
  }

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      {rows.map((k) => <KategorieRow key={k.id} item={k} />)}
      {rows.length === 0 && (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
          Noch keine Kategorien — lege die erste an.
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Neue Kategorie…"
          value={neu}
          onChange={(e) => setNeu(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }}
        />
        <IconButton color="primary" onClick={() => void add()} disabled={!neu.trim() || createMut.isPending} aria-label="Kategorie hinzufügen">
          <AddIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}

function KategorieRow({ item }: { item: AnforderungKategorie }) {
  const updateMut = useUpdateAnforderungKategorie();
  const deleteMut = useDeleteAnforderungKategorie();
  const { notify } = useNotifications();
  const [name, setName] = useState(item.name);

  const commitName = async () => {
    const v = name.trim();
    if (!v || v === item.name) { setName(item.name); return; }
    try { await updateMut.mutateAsync({ id: item.id, data: { name: v } }); }
    catch (e) { notify(formatApiError(e), 'error'); setName(item.name); }
  };

  const toggleActive = async (active: boolean) => {
    try { await updateMut.mutateAsync({ id: item.id, data: { active } }); }
    catch (e) { notify(formatApiError(e), 'error'); }
  };

  const remove = async () => {
    try { await deleteMut.mutateAsync(item.id); }
    catch (e) { notify(formatApiError(e), 'error'); }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        variant="standard"
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => void commitName()}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
        sx={{ opacity: item.active ? 1 : 0.5 }}
      />
      <Tooltip title={item.active ? 'Aktiv — in den Dropdowns wählbar' : 'Inaktiv — ausgeblendet'}>
        <FormControlLabel
          control={<Switch size="small" checked={item.active} onChange={(e) => void toggleActive(e.target.checked)} />}
          label=""
          sx={{ m: 0 }}
        />
      </Tooltip>
      <IconButton size="small" color="error" onClick={() => void remove()} disabled={deleteMut.isPending} aria-label="Kategorie löschen">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}
